// Classifies whether an unhandled promise rejection is worth reporting to the
// backend error log, or is benign noise that should be dropped.
//
// Dropped as noise:
// - axios-canceled requests (the user navigated away before the request resolved)
// - "Network Error" while the browser reports it is offline (flaky / lost connection)
// - 401/403 HTTP errors (expected authorization outcomes, e.g. opening a module
//   not assigned to the user via a stale link). Call sites already surface these
//   with an ErrorDialog; they reach the global handler only because the refresh/
//   retry machinery rejects a promise nobody awaits, so they are not real crashes.
// - media play() interruptions (AbortError): the browser rejects a pending
//   video.play() promise when pause()/a new load happens first — e.g. video.js
//   pausing or disposing while the user navigates away or taps pause quickly.
// - refused Picture-in-Picture requests: video.js shows its PiP button whenever
//   the browser supports PiP in general, but Safari rejects the request for a
//   specific video element (e.g. before its metadata is loaded) and video.js
//   does not catch that rejection. Nothing breaks, PiP just does not open.
// - rejections thrown by a browser extension (T00EUD33ZL9G): extensions that
//   wrap window.fetch reject promises of their own that nobody catches. When the
//   topmost stack frame lies in chrome-extension:// (or the Firefox/Safari
//   equivalent), the error is not ours. If our code is on top, it stays reported.
// - native fetch network errors ("Failed to fetch") while the browser reports it
//   is offline — the same rule as for axios' "Network Error" above.
//
// Everything else (5xx, other HTTP status errors, TypeErrors, etc.) is still reported.
const EXTENSION_FRAME = /^(chrome|moz|safari(-web)?)-extension:\/\//;
// Chrome/Edge: "    at fn (url:1:2)" or "    at url:1:2"; Firefox/Safari: "fn@url:1:2".
const FRAME_URL = /^\s*at (?:.*\()?([^()\s]+:\d+:\d+)\)?\s*$|^[^@\s]*@(.+:\d+:\d+)$/;

const topFrameIsExtension = (stack: unknown): boolean => {
    if (typeof stack !== 'string') {
        return false;
    }
    for (const line of stack.split('\n')) {
        const match = FRAME_URL.exec(line);
        if (match) {
            return EXTENSION_FRAME.test(match[1] ?? match[2] ?? '');
        }
    }
    return false;
};

// Chrome "Failed to fetch", Firefox "NetworkError when attempting to fetch
// resource.", Safari "Load failed".
const FETCH_NETWORK_ERROR = /^(Failed to fetch|NetworkError when attempting to fetch resource\.?|Load failed)$/;

export const shouldReportRejection = (reason: any): boolean => {
    if (!reason) {
        return true;
    }

    // Request aborted — e.g. navigation away mid-flight. Never actionable.
    if (reason.code === 'ERR_CANCELED' || reason.name === 'CanceledError') {
        return false;
    }

    // No HTTP response received while the browser is offline. A network error
    // while online may be a real backend/CORS issue, so that stays reported.
    const isNetworkError = reason.code === 'ERR_NETWORK' || reason.message === 'Network Error';
    if (isNetworkError && typeof navigator !== 'undefined' && navigator.onLine === false) {
        return false;
    }

    // T00EUD33ZL9G — the same for native fetch (fetch-event-source, the
    // notification stream).
    const isFetchNetworkError = reason.name === 'TypeError' && typeof reason.message === 'string'
        && FETCH_NETWORK_ERROR.test(reason.message);
    if (isFetchNetworkError && typeof navigator !== 'undefined' && navigator.onLine === false) {
        return false;
    }

    // T00EUD33ZL9G — thrown inside a browser extension, not in our code.
    if (topFrameIsExtension(reason.stack)) {
        return false;
    }

    // Interrupted media playback ("The play() request was interrupted by a call
    // to pause()/a new load request"). Normal browser behavior, never actionable.
    if (reason.name === 'AbortError' && typeof reason.message === 'string'
        && reason.message.includes('play() request was interrupted')) {
        return false;
    }

    // Picture-in-Picture refused by the browser (video.js PiP toggle). Not a bug
    // in our code and nothing the user loses beyond PiP not opening.
    if (typeof reason.message === 'string' && reason.message.includes('Picture-in-Picture')) {
        return false;
    }

    // Authentication/permission denials are expected server-side decisions, not
    // frontend bugs. Drop them so the error log stays focused on real problems.
    const status = reason.response?.status ?? reason.status;
    if (status === 401 || status === 403) {
        return false;
    }

    return true;
};

// Extra context pulled off a rejection reason to make minified reports easier to
// triage even before source-map resolution: the error type (TypeError, ...) and,
// for axios errors, the failing request and HTTP status. All fields are optional
// — a plain rejected value (string, undefined) yields nulls.
export type RejectionContext = {
    name: string | null;
    requestMethod: string | null;
    requestUrl: string | null;
    status: number | null;
};

export const describeRejection = (reason: any): RejectionContext => {
    if (!reason || typeof reason !== 'object') {
        return {name: null, requestMethod: null, requestUrl: null, status: null};
    }

    return {
        name: typeof reason.name === 'string' ? reason.name : null,
        requestMethod: typeof reason.config?.method === 'string' ? reason.config.method.toUpperCase() : null,
        requestUrl: typeof reason.config?.url === 'string' ? reason.config.url : null,
        status: reason.response?.status ?? reason.status ?? null,
    };
};
